<template>
  <section
    ref="heroRoot"
    class="hero-root relative pt-12"
  >
    <!-- grid -->
    <div
      class="pointer-events-none absolute inset-0 bg-center bg-grid-white/10 bg-grid-16 [mask-image:radial-gradient(white,transparent_85%)]" />

    <div class="relative mx-auto flex max-w-7xl flex-col justify-center gap-4 px-4 py-8 sm:px-6 lg:px-8 lg:py-16">
      <!-- spotlight -->
      <div
        class="absolute -top-8 left-1/2 size-72 -translate-x-1/2 rounded-full bg-white/25 blur-[120px] lg:-top-8 lg:size-[32rem] lg:blur-[200px]" />

      <!-- profile picture -->
      <HomeProfilePicture class="hero-in" />

      <div class="z-20 flex flex-col items-center justify-center">
        <!-- title -->
        <div class="font-geist">
          <h1
            class="hero-in hero-title mx-auto font-bold text-center text-balance bg-gradient-to-b from-white to-white/50 bg-clip-text text-4xl text-transparent sm:text-5xl lg:max-w-6xl lg:text-6xl xl:text-7xl tracking-tight leading-tight xl:leading-[1.08]">
            <slot name="hero_title" mdc-unwrap="p" />
          </h1>

          <!-- subtitle -->
          <p class="hero-in hero-sub mx-auto mt-4 max-w-xl text-center text-lg antialiased text-balance text-white/60">
            <slot name="hero_subtitle" mdc-unwrap="p" />
          </p>
        </div>

        <!-- cta -->
        <HomeCTA class="hero-in hero-cta mt-8" />

        <!-- social -->
        <HomeSocial class="hero-in hero-social" />

        <HomeMarquee class="hero-in hero-marquee" />

        <!-- projects: no entrance animation here, a transform would break the sticky section -->
        <div class="mt-12 w-full lg:mt-16">
          <HomeProjects />
        </div>

        <Divider class="my-9" />

        <!-- the map of Normandy, from Caen to Gouville-sur-Mer -->
        <HomeLocalMap />

        <HomeFaq class="mt-24 mb-8" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { Collections } from '@nuxt/content'
  import gsap from 'gsap'

  // Entrance of the hero: one sequence on load, nothing keeps moving afterwards.
  const heroRoot = ref<HTMLElement>()
  let heroMotion: gsap.MatchMedia | undefined

  onMounted(() => {
    heroMotion = gsap.matchMedia()
    heroMotion.add('(prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(heroRoot.value)
      heroRoot.value!.classList.add('hero-ready')

      gsap.timeline({ defaults: { ease: 'power3.out', lazy: false } })
        .from(q('.hero-disc'), { scale: 0.6, opacity: 0, duration: 0.8, ease: 'back.out(1.6)' }, 0)
        .from(q('.hero-portrait'), { yPercent: 45, opacity: 0, duration: 1 }, 0.1)
        .fromTo(q('.hero-title'), { clipPath: 'inset(0% 0% 100% 0%)', y: 24 }, { clipPath: 'inset(-10% -5% -10% -5%)', y: 0, duration: 0.9, clearProps: 'clipPath,transform' }, 0.35)
        .from(q('.hero-sub'), { y: 16, opacity: 0, duration: 0.7 }, 0.6)
        .from(q('.hero-cta > *'), { y: 16, opacity: 0, duration: 0.6, stagger: 0.08 }, 0.75)
        .from(q('.hero-social > *'), { y: 10, opacity: 0, duration: 0.5, stagger: 0.06 }, 0.95)
        .from(q('.hero-marquee'), { opacity: 0, duration: 0.8, ease: 'power1.out' }, 1.05)
    }, heroRoot.value)
  })

  onBeforeUnmount(() => heroMotion?.revert())

  const route = useRoute()
  const { locale } = useI18n()
  const collection = computed(() => `content_${locale.value}` as keyof Collections)
  const { profile, seo, socials } = useAppConfig()

  const { data: page } = await useAsyncData(route.path, () => queryCollection(collection.value).path(route.path).first())

  // Generate JSON-LD for LocalBusiness if it's a local landing page
  if (route.path.includes('creation-site-web')) {
    const areas = (seo as { localAreas?: string[] }).localAreas ?? []
    const imageUrl = profile.picture?.startsWith('http')
      ? profile.picture
      : `${seo.url}${profile.picture}`

    useHead({
      script: [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            name: `${profile.name} - Développeur Web`,
            image: imageUrl,
            '@id': `${seo.url}#local-business`,
            url: seo.url,
            telephone: profile.phone,
            email: profile.email,
            sameAs: Object.values(socials ?? {}),
            address: {
              '@type': 'PostalAddress',
              addressLocality: (page.value as { title?: string })?.title?.split(' à ')[1]?.split(' - ')[0] || 'Gouville-sur-Mer',
              addressRegion: (seo as { localRegion?: string }).localRegion || 'Normandie',
              addressCountry: (seo as { localCountry?: string }).localCountry || 'FR',
            },
            areaServed: areas.map((name) => ({ '@type': 'Place', name })),
            serviceType: [
              'Création de site vitrine',
              'Site e-commerce',
              'Développement SaaS',
              'Maintenance et refonte',
              'SEO local',
            ],
          }),
        },
      ],
    })
  }
</script>

<style>
  /* Hidden until the entrance starts, with a fallback in case the script never runs. */
  @media (prefers-reduced-motion: no-preference) {
    .hero-root:not(.hero-ready) .hero-in {
      opacity: 0;
      animation: hero-fallback 0.3s 2.5s forwards;
    }
  }

  @keyframes hero-fallback {
    to {
      opacity: 1;
    }
  }
</style>
